import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Row, Col, Select, DatePicker, message } from 'antd';
import DataTable from 'react-data-table-component';

import ActivityService from 'services/activities';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import queryString from 'query-string';

import PdButton from 'components/pd-button';

import { get } from 'lodash';
import { formatStatus } from 'utils';

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;

const dateFormat = 'YYYY/MM/DD';

export function Activities() {
  const location = useLocation();
  const [loadStatus, setLoadStatus] = useState(true);
  const [tableList, setTableList] = useState([]);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(1);
  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);
  const [admins, setAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [startDatePick, setStartDate] = useState('');
  const [endDatePick, setEndDate] = useState('');
  const FIELDS = {
    ID: 'id',
    ACTOR: 'actor',
    VERB: 'verb',
    OBJECT: 'object',
    OBJECT_TYPE: 'object_type',
    MESSAGE: 'message',
    TIMESTAMP: 'created_at',
  };

  const ACTOR_FIELDS = {
    ID: 'id',
    NAME: 'name',
  };
  const OBJECT_FIELDS = {
    ID: 'id',
    TITLE: 'title',
  };

  const customElement = (row) => (
    <div className="button-table">
      {row.object !== '' && row.notEmptyObject && (
        <Link to={`/${row.path}`}>
          <PdButton className="height-32 w-full" type="primary">
            View Object 
          </PdButton>
        </Link>
      )}
    </div>
  );

  customElement.displayName = 'customButton';

  const [columns] = useState([
    {
      name: 'Time',
      selector: row => row.timestamp,
      format: (row) => {
        return dayjs(row.timestamp).format('YYYY-MM-DD HH:mm');
      },
      width: '20%',
    },
    {
      name: 'Admin',
      selector: row => row.admin,
      width: '18%',
    },
    {
      name: 'Action',
      selector: row => row.action,
      width: '19%',
    },
    {
      name: 'Object',
      selector: row => row.object,
      width: '29%',
    },
    {
      name: '',
      cell: customElement,
      width: '15%',
    },
  ]);

  function getDesc(activity) {
    const verb = get(activity, FIELDS.VERB);
    if (verb === 'login' || verb === 'logout') return '';
    else {
      const type = get(activity, FIELDS.OBJECT_TYPE);
      const title = get(activity, [FIELDS.OBJECT, OBJECT_FIELDS.ID], '');
      if (!title) return `${formatStatus(type)}`;
      else return `${formatStatus(type)}: ${title}`;
    }
  }

  function getPath(activity) {
    const verb = get(activity, FIELDS.VERB);
    const type = get(activity, FIELDS.OBJECT_TYPE);
    const id = get(activity, `${FIELDS.OBJECT}.${OBJECT_FIELDS.ID}`);
    var link = type;
    if (verb === 'login' || verb === 'logout') return '';
    switch (type) {
      case 'product_collections':
        link = 'collections';
        break;
      default:
        link = type;
    }
    if (id !== undefined) {
      if (type === 'orders') link += `/detail?id=${id}`;
      else if (type === 'rent_duration') link += 's';
      else if (type === 'blackout_dates') return link;
      else link += `/edit?id=${id}`;
    }
    return link;
  }

  function getAction(verb) {
    var action = '';
    switch (verb) {
      case 'update_data':
        action = 'Edit';
        break;
      case 'update_order':
        action = 'Change Sorting';
        break;
      case 'process_order':
        action = 'Accept Order';
        break;
      case 'confirm_shipment':
        action = 'Confirm Tracking';
        break;
      case 'update_shipment':
        action = 'Update Tracking';
        break;
      case 'order_reports':
        action = 'Download Report';
        break;
      case 'edit_relations':
        action = 'Edit Sizing';
        break;
      case 'shipping_label':
        action = 'Print Shipping Label';
        break;
      case 'mark_late_order':
        action = 'Mark order as late';
        break;
      default:
        action = formatStatus(verb);
        break;
    }
    return action;
  }

  async function fetchActivityList(
    page = 1,
    total = 10,
    startDate,
    endDate,
    filter
  ) {
    setLoadStatus(true);
    try {
      const { data } = await ActivityService.getActivityList({
        page,
        total,
        startDate,
        endDate,
        filter,
      });
      const activityData = data.data;
      const metadata = data.metadata;
      setNextPage(metadata.next_cursor);
      setLoadStatus(false);
      const tableData = activityData.map((activity) => {
        return {
          action: getAction(get(activity, FIELDS.VERB, '')),
          admin: get(activity, `${FIELDS.ACTOR}.${ACTOR_FIELDS.NAME}`, ''),
          notEmptyObject: get(
            activity,
            `${FIELDS.OBJECT}.${OBJECT_FIELDS.ID}`,
            ''
          ),
          path: getPath(activity),
          object: getDesc(activity),
          timestamp: get(activity, FIELDS.TIMESTAMP, ''),
        };
      });
      setTableList(tableData);
    } finally {
      setLoadStatus(false);
    }
  }
  async function fetchAdminList() {
    try {
      const { data } = await ActivityService.fetchAdmins();
      const AdminList = data.data;
      setAdmins(AdminList);
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    }
  }
  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate(`/activities?${stringified}`);
  }

  function onChangeDate(dates, datesString) {
    const startDate = datesString[0];
    if (startDate === '') {
      delete parsed['startDate'];
    } else {
      parsed['startDate'] = startDate;
    }

    const endDate = datesString[1];
    if (endDate === '') {
      delete parsed['endDate'];
    } else {
      parsed['endDate'] = endDate;
    }

    const stringified = queryString.stringify(parsed);
    navigate(`/activities?${stringified}`);
  }
  function filterActivities(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    const stringified = queryString.stringify(parsed);
    navigate(`/activities?${stringified}`);
  }
  function resetFilter() {
    delete parsed['page'];
    delete parsed['startDate'];
    delete parsed['endDate'];
    delete parsed['limit'];
    delete parsed['actor_id'];

    const stringified = queryString.stringify(parsed);
    navigate(`/activities?${stringified}`);
  }
  useEffect(() => {
    fetchAdminList();
  }, []);
  /* eslint-disable */
  useEffect(() => {
    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate(`/activities?${stringified}`);
      } else {
        setPage(parsed['page']);
      }
    } else {
      setPage('1');
    }
    if (parsed['startDate']) {
      setStartDate(parsed['startDate']);
    } else {
      setStartDate('');
    }

    if (parsed['endDate']) {
      setEndDate(parsed['endDate']);
    } else {
      setEndDate('');
    }
    if (parsed['actor_id']) {
      setSelectedAdmin(parsed['actor_id']);
    } else {
      setSelectedAdmin(0);
    }

    fetchActivityList(
      parsed['page'],
      10,
      parsed['startDate'],
      parsed['endDate'],
      parsed['actor_id']
    );
  }, [
    parsed['page'],
    parsed['startDate'],
    parsed['endDate'],
    parsed['actor_id'],
  ]);
  /* eslint-enable */

  return (
    <Layout className="pd-cms-activities main-container">
      <div className="content">
        <div className="pageTitle">Activities</div>
        <Content className="pageContent">
          <Row gutter={18}>
            <Col span={24}>
              <Row gutter={18}>
                <Col span={7} className="mt-1">
                  <RangePicker
                    className="w-full"
                    format={dateFormat}
                    onChange={onChangeDate}
                    value={[
                      startDatePick === ''
                        ? ''
                        : dayjs(startDatePick, dateFormat),
                      endDatePick === '' ? '' : dayjs(endDatePick, dateFormat),
                    ]}
                  />
                </Col>
                <Col span={4} className="mt-1">
                  <Select
                    style={{ width: '100%' }}
                    value={Number(selectedAdmin)}
                    onChange={(val) => filterActivities('actor_id', val)}>
                    <Option value={0}>All Admins</Option>
                    {admins.map((admin, i) => (
                      <Option key={i} value={admin.id}>
                        {`${admin.first_name} ${admin.last_name ?? ''}`}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={4} className="mt-1">
                  <span className="reset-filter" onClick={resetFilter}>
                    Reset Filter
                  </span>
                </Col>
              </Row>
            </Col>

            {!loadStatus && (
              <>
                <Col xs={24}>
                  <DataTable
                    columns={columns}
                    data={tableList}
                    progressPending={loadStatus}
                    noHeader
                    highlightOnHover
                    striped
                  />
                </Col>

                <Col span={8} offset={8}>
                  <div className="center-button">
                    {page !== '1' && (
                      <PdButton
                        type="primary"
                        outline
                        style={{ height: 32 }}
                        onClick={() => changePage(Number(page) - 1)}>
                        Previous
                      </PdButton>
                    )}
                    {nextPage !== -1 && (
                      <PdButton
                        type="primary"
                        outline
                        style={{ height: 32 }}
                        onClick={() => changePage(Number(page) + 1)}>
                        Next
                      </PdButton>
                    )}
                  </div>
                </Col>
              </>
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { Activities };
